import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { plain } from "@/lib/plain";
import { cn } from "@/lib/util";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Line, LineChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { TrendingUp, TrendingDown, DollarSign, Activity, ShieldCheck, Loader2 } from "lucide-react";

const fmtDate = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString("es-ES", { month: "short", day: "numeric" }); }
  catch { return String(v); }
};

const usd = (n) => `$${(n || 0).toLocaleString("en-US")}`;

export function RoiDashboard({ clients }) {
  const [clientId, setClientId] = useState(clients?.[0]?.id || "");
  const [range, setRange] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientId && clients?.length) setClientId(clients[0].id);
  }, [clients, clientId]);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const r = await api.get(`/reports/roi/${clientId}?range=${range}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar ROI");
    } finally { setLoading(false); }
  }, [clientId, range]);

  useEffect(() => { load(); }, [load]);

  const surfaceData = useMemo(() =>
    (data?.attack_surface?.series || []).map(p => ({ date: fmtDate(p.date), assets: p.exposed_assets })),
  [data]);

  const mttrData = useMemo(() =>
    (data?.mttr_trend?.series || []).map(p => ({ date: fmtDate(p.date),
      mttr: p.mttr_hours, samples: p.sample_size })),
  [data]);

  if (!data && loading) return (
    <div className="border border-border bg-card/60 p-8 text-center font-mono text-sm text-muted-foreground">
      <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Calculando métricas de ROI…
    </div>
  );

  return (
    <div className="space-y-6" data-testid="roi-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <select value={clientId} onChange={e => setClientId(e.target.value)}
                  className="bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
                  data-testid="roi-client-selector">
            {clients.map(c => <option key={c.id} value={c.id}>{plain(c.name, `#${c.id.slice(0,8)}`)}</option>)}
          </select>
          <div className="flex items-center border border-border" data-testid="roi-range-selector">
            {[30, 90, 365].map(r => (
              <button key={r} onClick={() => setRange(r)}
                      className={cn("font-mono text-[10px] tracking-widest uppercase px-3 py-2",
                        range === r ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}
                      data-testid={`roi-range-${r}`}>
                {r === 365 ? "1a" : `${r}d`}
              </button>
            ))}
          </div>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Periodo: {data?.since ? new Date(data.since).toLocaleDateString() : "—"} → {data?.until ? new Date(data.until).toLocaleDateString() : "—"}
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard icon={ShieldCheck} label="Críticos resueltos"
                   value={data.critical_resolved.current}
                   deltaPct={data.critical_resolved.delta_pct}
                   subtitle={`${data.critical_resolved.previous} periodo anterior`}
                   testid="roi-kpi-critical" />
          <KpiCard icon={Activity} label="MTTR actual (h)"
                   value={data.mttr_trend.current ?? "—"}
                   deltaPct={data.mttr_trend.delta_pct}
                   invertTrend
                   subtitle={data.mttr_trend.previous != null ? `${data.mttr_trend.previous} h anteriores` : "sin base"}
                   testid="roi-kpi-mttr" />
          <KpiCard icon={TrendingDown} label="Superficie ataque"
                   value={data.attack_surface.ending_count}
                   deltaPct={data.attack_surface.delta_pct}
                   invertTrend
                   subtitle={`de ${data.attack_surface.starting_count} activos`}
                   testid="roi-kpi-surface" />
          <KpiCard icon={DollarSign} label="Pérdida evitada (est.)"
                   value={usd(data.roi_estimate.avoided_loss_usd)}
                   subtitle={`${data.roi_estimate.resolved_critical} crít. + ${data.roi_estimate.resolved_high} alt.`}
                   testid="roi-kpi-savings" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="MTTR — evolución" subtitle="Horas promedio por ventana"
                    testid="roi-chart-mttr" empty={mttrData.every(p => p.mttr == null)}>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={mttrData} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fill: "#888", fontSize: 10 }} />
              <YAxis tick={{ fill: "#888", fontSize: 10 }} />
              <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 12 }}
                       formatter={(v, name) => name === "mttr" ? `${v ?? "—"} h` : v} />
              <Line type="monotone" dataKey="mttr" stroke="#22d3ee" strokeWidth={2} connectNulls
                    dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Superficie de ataque — activos expuestos"
                    subtitle="Subdominios monitoreados over time"
                    testid="roi-chart-surface" empty={surfaceData.every(p => p.assets === 0)}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={surfaceData} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="surface" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f97316" stopOpacity={0.6}/>
                  <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fill: "#888", fontSize: 10 }} />
              <YAxis tick={{ fill: "#888", fontSize: 10 }} />
              <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 12 }} />
              <Area type="monotone" dataKey="assets" stroke="#f97316" fill="url(#surface)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {data && (
        <div className="border border-border bg-card/60 p-5 space-y-3" data-testid="roi-risk-summary">
          <div className="overline">Reducción de riesgo</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 font-mono text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Rating inicial</div>
              <div className="font-display text-3xl">{data.risk_reduction.initial_score ?? "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Rating actual</div>
              <div className="font-display text-3xl">{data.risk_reduction.current_score ?? "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Δ puntos</div>
              <div className={cn("font-display text-3xl",
                (data.risk_reduction.delta_points ?? 0) > 0 ? "text-sev-low" :
                (data.risk_reduction.delta_points ?? 0) < 0 ? "text-sev-critical" : "text-muted-foreground")}>
                {data.risk_reduction.delta_points != null
                  ? `${data.risk_reduction.delta_points > 0 ? "+" : ""}${data.risk_reduction.delta_points}`
                  : "—"}
              </div>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            La pérdida evitada usa un modelo abierto y transparente: ${data.roi_estimate.cost_per_critical_usd.toLocaleString()} por crítico + ${data.roi_estimate.cost_per_high_usd.toLocaleString()} por alto (benchmark IBM 2024). Puedes ajustarlo por tenant desde Ajustes → ROI.
          </p>
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, deltaPct, subtitle, testid, invertTrend }) {
  const positive = deltaPct != null ? (invertTrend ? deltaPct < 0 : deltaPct > 0) : null;
  const TrendIcon = deltaPct != null && ((invertTrend ? deltaPct < 0 : deltaPct > 0)) ? TrendingUp : TrendingDown;
  return (
    <div className="border border-border bg-card/60 p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="overline flex items-center gap-1"><Icon className="w-3 h-3" />{label}</div>
        {deltaPct != null && (
          <span className={cn("font-mono text-[10px] flex items-center gap-0.5",
            positive ? "text-sev-low" : "text-sev-critical")}>
            <TrendIcon className="w-3 h-3" />
            {deltaPct > 0 ? "+" : ""}{deltaPct}%
          </span>
        )}
      </div>
      <div className="font-display text-3xl">{value}</div>
      {subtitle && <div className="font-mono text-[10px] text-muted-foreground mt-1">{subtitle}</div>}
    </div>
  );
}

function ChartCard({ title, subtitle, children, testid, empty }) {
  return (
    <div className="border border-border bg-card/60" data-testid={testid}>
      <div className="px-4 py-3 border-b border-border">
        <div className="text-sm">{title}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{subtitle}</div>
      </div>
      <div className="p-3">
        {empty ? (
          <div className="h-[220px] flex items-center justify-center text-xs font-mono text-muted-foreground">
            Sin datos en el periodo seleccionado.
          </div>
        ) : children}
      </div>
    </div>
  );
}
